import frontendLogger from "../utils/frontendLogger.js";
import useAuthUser from "../hooks/useAuthUser.js";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../pages/css/App.css";
import "../pages/css/MainPage.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import { API_BASE_URL } from "../app_config.js";

const getAuthToken = () => localStorage.getItem("token");

export default function DeleteWhiskys() {
  const { username, role, loading: authLoading } = useAuthUser();
  const navigate = useNavigate();
  const [whiskys, setWhiskys] = useState([]);
  const [selectedWhiskyId, setSelectedWhiskyId] = useState("");
  const [selectedWhiskyName, setSelectedWhiskyName] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  // Redirect wenn nicht Admin
  useEffect(() => {
    if (!authLoading && role !== "admin") {
      navigate("/main");
    }
  }, [role, navigate, authLoading]);

  // Whiskys laden
  useEffect(() => {
    const fetchWhiskys = async () => {
      try {
        const token = getAuthToken();
        const response = await fetch(`${API_BASE_URL}/api/whisky`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) throw new Error("Fehler beim Laden der Whiskys");

        const data = await response.json();
        frontendLogger.info("Fetched whiskys:", data);

        // Filtere gelöschte Whiskys aus
        const activeWhiskys = data.filter(
          (w) => !w.name?.startsWith("deleted_whisky"),
        );
        setWhiskys(activeWhiskys);
      } catch (error) {
        frontendLogger.error("Error fetching whiskys:", error);
        setMessage("Fehler beim Laden der Whiskys");
      }
    };

    fetchWhiskys();
  }, []);

  const handleWhiskySelect = (e) => {
    const id = e.target.value;
    setSelectedWhiskyId(id);

    if (id) {
      const selected = whiskys.find((w) => w.id === parseInt(id));
      setSelectedWhiskyName(selected?.name || "");
    } else {
      setSelectedWhiskyName("");
    }
  };

  const handleDeleteClick = () => {
    if (!selectedWhiskyId) {
      setMessage("Bitte wähle einen Whisky aus");
      return;
    }
    setShowConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    setShowConfirmation(false);

    try {
      const token = getAuthToken();
      const response = await fetch(
        `${API_BASE_URL}/api/whisky/${selectedWhiskyId}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (!response.ok) {
        throw new Error("Fehler beim Löschen des Whiskys");
      }

      const data = await response.json();
      frontendLogger.info("Whisky deleted:", data);

      setMessage(
        data.marked
          ? `✓ "${selectedWhiskyName}" wurde als gelöscht markiert (erscheint noch in Tastings als "deleted_whisky")`
          : `✓ "${selectedWhiskyName}" wurde vollständig gelöscht`,
      );

      // Aktualisiere die Liste
      setWhiskys(whiskys.filter((w) => w.id !== parseInt(selectedWhiskyId)));
      setSelectedWhiskyId("");
      setSelectedWhiskyName("");
    } catch (error) {
      frontendLogger.error("Error deleting whisky:", error);
      setMessage("Fehler beim Löschen des Whiskys");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowConfirmation(false);
  };

  const handleBack = () => {
    navigate("/main");
  };

  return (
    <div className="page-layout">
      <div className="menu-container">
        <div className="title-box">
          <p>Willkommen bei DRAM</p>
        </div>
        <div className="menu-box">
          <h1>{username}</h1>
        </div>

        <nav className="nav">
          <button className="nav-button" onClick={handleBack}>
            Zurück
          </button>
        </nav>

        <img
          src={require("../assets/logo.png")}
          alt="Versionsinformationen anzeigen"
          className="whisky-image"
          role="button"
          tabIndex={0}
        />
      </div>

      <div className="content-container">
        <div className="search-form-group">
          <form
            id="deleteForm"
            onSubmit={(e) => {
              e.preventDefault();
              handleDeleteClick();
            }}
          >
            <h2 style={{ textAlign: "center", marginBottom: "20px" }}>
              Whisky löschen
            </h2>

            <label htmlFor="whisky-select">
              Wähle einen Whisky zum Löschen:
            </label>
            <select
              id="whisky-select"
              value={selectedWhiskyId}
              onChange={handleWhiskySelect}
              style={{
                padding: "10px",
                borderRadius: "5px",
                border: "1px solid #c69c6d",
                fontSize: "14px",
              }}
            >
              <option value="">-- Whisky auswählen --</option>
              {whiskys.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name} ({w.alcohol_percent}%)
                </option>
              ))}
            </select>

            {message && (
              <div
                style={{
                  padding: "12px",
                  backgroundColor:
                    message.startsWith("✓") || message.startsWith("Whisky")
                      ? "#e8f5e9"
                      : "#ffebee",
                  color:
                    message.startsWith("✓") || message.startsWith("Whisky")
                      ? "#2e7d32"
                      : "#c62828",
                  borderRadius: "5px",
                  marginTop: "10px",
                  fontSize: "14px",
                  textAlign: "center",
                }}
              >
                {message}
              </div>
            )}

            <button
              type="submit"
              style={{
                padding: "12px 20px",
                background: "linear-gradient(135deg, #8b5e3c 0%, #5c3a21 100%)",
                border: "1px solid #c69c6d",
                borderRadius: "5px",
                color: "#f5e6d3",
                fontWeight: "600",
                cursor: selectedWhiskyId ? "pointer" : "not-allowed",
                fontSize: "14px",
                opacity: selectedWhiskyId ? 1 : 0.5,
                transition: "transform 0.15s ease, box-shadow 0.15s ease",
              }}
              disabled={!selectedWhiskyId || loading}
              onMouseEnter={(e) => {
                if (selectedWhiskyId) {
                  e.target.style.transform = "translateY(-2px)";
                  e.target.style.boxShadow =
                    "0 6px 15px rgba(60, 40, 20, 0.25)";
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "none";
              }}
            >
              {loading ? "Wird gelöscht..." : "Löschen"}
            </button>
          </form>
        </div>
      </div>

      {showConfirmation && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h2>Whisky wirklich löschen?</h2>
            <p style={{ marginBottom: "20px" }}>
              Bist du sicher, dass du den Whisky{" "}
              <strong>"{selectedWhiskyName}"</strong> löschen möchtest?
            </p>
            <p
              style={{ fontSize: "0.9em", color: "#666", marginBottom: "20px" }}
            >
              {selectedWhiskyName?.startsWith("deleted_whisky")
                ? "Hinweis: Dieser Whisky wird noch in Tastings angezeigt."
                : "Falls dieser Whisky in Tastings verwendet wird, wird er dort als 'deleted_whisky' angezeigt."}
            </p>

            <div
              style={{
                display: "flex",
                gap: "10px",
                justifyContent: "flex-end",
              }}
            >
              <button
                style={{
                  padding: "10px 20px",
                  background: "#ccc",
                  border: "1px solid #999",
                  borderRadius: "5px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "600",
                  transition: "background 0.2s ease",
                }}
                onMouseEnter={(e) => (e.target.style.background = "#bbb")}
                onMouseLeave={(e) => (e.target.style.background = "#ccc")}
                onClick={handleCancel}
              >
                Nein, abbrechen
              </button>
              <button
                style={{
                  padding: "10px 20px",
                  background:
                    "linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)",
                  color: "#fff",
                  border: "1px solid #b71c1c",
                  borderRadius: "5px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "600",
                  transition: "transform 0.15s ease, box-shadow 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = "translateY(-2px)";
                  e.target.style.boxShadow = "0 6px 15px rgba(0, 0, 0, 0.3)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = "none";
                }}
                onClick={handleConfirmDelete}
                disabled={loading}
              >
                Ja, löschen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
